<template>
  <o-modal
    ref="modal"
    :title="$t('components.message.elements.attachments.types.upload.action.title')"
    :options="[
      { text: $t('general.cancel'), type: 'cancel'},
      { text: $t('general.attach'), type: 'confirm', callback: handleConfirm, disabled: !valid },
    ]"
    :loading="loading"
    modal-class="relative m-2 bg-gray-100 dark:bg-gray-900 rounded-lg shadow-lg pointer-events-auto overflow-hidden sm:max-w-2xl sm:w-full h-full max-h-lg"
    content-class="mx-auto max-w-2xl h-full min-h-0 flex flex-col"
  >
    <template #body>
      <o-tabs class="min-h-0" :show-arrows="false" nav-class="border-b border-gray-300 dark:border-gray-800" @changed="handleChangedTab">
        <o-tab id="upload" :name="$t('general.uploadAttachment')" icon="upload">
          <o-form v-if="!loading" ref="form" class="px-3 block">
            <o-field-group :name="$t('general.attachment')" class="block pb-4 mb-4 border-b">
              <o-upload v-model="_upload" v-model:caption="upload.caption" />
            </o-field-group>

            <!--<o-field-group label="Add to Asset Library" info="Save this file to your asset library to easily use it again in future campaigns" class="block pb-3">
              <toggle-field v-model="upload.save" />
            </o-field-group>-->

            <o-field-group v-if="upload.save" :label="$t('asset.fileName')" :info="$t('asset.fileNameInfo')" class="block pb-3">
              <text-field v-model="upload.caption" />
            </o-field-group>
          </o-form>
        </o-tab>
        <o-tab id="assets" :name="$t('asset.library')" icon="library">
          <div class="h-full min-h-0 flex flex-col overflow-hidden">
            <div class="flex items-center justify-between px-3 mb-4 pb-2 -mt-2 border-b flex-shrink-0">
              <text-field
                v-model="assets.filters.search"
                :placeholder="$t('asset.search')"
                leading="search"
                clear
                class="w-72"
                @input="handleFilterChange"
              />

              <select-field
                v-model="assets.filters.dataType"
                :options="[
                  { text: $t('general.all'), value: null },
                  { text: $t('asset.images'), value: 0 },
                  { text: $t('asset.files'), value: 1 }
                ]"
                class="w-36"
                @change="handleFilterChange"
              />
            </div>
            <upload-pagination :pager="assets.pager" class="flex-shrink min-h-0 h-full flex-grow border-b">
              <template #default="{items}">
                <div class="flex items-start flex-wrap gap-3 mb-8 p-1">
                  <upload-grid
                    v-for="(item, index) in items"
                    :key="item.idPhoto"
                    :upload="item"
                    :class="{ 'ring-offset-2 ring' : assets.selected === item }"
                    @click="handleSelectAsset(item)"
                  />
                </div>
              </template>

              <template #empty>
                <div class="p-6">
                  <p class="text-center text-gray-800 font-semibold">
                    {{ $t('asset.noUploads') }}
                  </p>
                  <p class="text-center text-sm text-gray-600">
                    {{ assets.filters.search || assets.filters.dataType !== null ? $t('asset.adjustFilters') : $t('asset.uploadInfo') }}
                  </p>
                </div>
              </template>
            </upload-pagination>
          </div>
        </o-tab>
      </o-tabs>
    </template>
  </o-modal>
</template>

<script setup lang="ts">
import debounce from 'lodash/debounce'
import { v4 as uuid } from 'uuid'

import UploadPagination from '@/components/upload/elements/Pagination.vue'
import UploadGrid from '@/components/upload/elements/Grid.vue'

import {
  OUpload,
  OProgress,
  OTabs,
  OTab,
} from '@/components/elements'

import {
  ToggleField,
  TextField,
  SelectField,
} from '@/components/fields'

import { EndlessListPager } from '~/_api/lib/pager'
import { CustomerMessageAttachment, CustomerMessageAttachmentType } from '~/_api/models/customerMessage'

const { $api, $notification } = useNuxtApp()

interface Props {
  portal?: string
  customer: Record<string, any>
  uploads?: Record<string, File>
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  uploads: () => ({}),
})

const emit = defineEmits<{
  (e: 'confirm', attachments: any[]): void
  (e: 'update:uploads', uploads: Record<string, File>): void
}>()

const modal = ref<any>()
const loading = ref(false)
const tab = ref('upload')

const upload = ref({
  file: null as File | null,
  save: true,
  caption: '',
})

const assets = ref({
  pager: null as any,
  selected: null as any,
  filters: {
    search: '',
    dataType: null as number | null,
  },
})

const _upload = computed({
  get () {
    if (upload.value.file instanceof File) {
      return upload.value.file
    }
    return null
  },
  set (value: File | null) {
    upload.value.file = value
  },
})

const isTabUpload = computed(() => tab.value === 'upload')
const isTabAssets = computed(() => tab.value === 'assets')

const valid = computed(() => {
  if (isTabUpload.value) {
    return !!upload.value.file
  }
  if (isTabAssets.value) {
    return !!assets.value.selected
  }
  return false
})

const open = () => {
  upload.value.file = null
  upload.value.caption = ''
  assets.value.filters = {
    search: '',
    dataType: null,
  }
  assets.value.pager = new EndlessListPager((page: number) => {
    return $api.marketing.uploads({
      page,
      search: assets.value.filters.search,
      dataType: assets.value.filters.dataType,
    })
  }, 20)

  assets.value.selected = null

  modal.value.open()
}

const handleFilterChange = debounce(async () => {
  assets.value.pager.reload()
}, 500)

const handleConfirm = async () => {
  loading.value = true

  try {
    let attachment: Record<string, any> = {
      type: CustomerMessageAttachmentType.upload,
    }

    // If adding an existing attachment
    if (isTabAssets.value) {
      attachment = {
        ...attachment,
        id: assets.value.selected.idPhoto,
        data: assets.value.selected,
      }
    }

    // If uploading a new attachment
    if (isTabUpload.value) {
      const id = uuid()

      attachment = {
        ...attachment,
        data: {
          file: upload.value.file,
          caption: upload.value.caption,
        },
        _uploads: {
          image: id,
          caption: upload.value.caption,
        },
      }

      emit('update:uploads', {
        ...props.uploads,
        [id]: upload.value.file,
      })
    }

    const attachments = [
      new CustomerMessageAttachment(attachment),
    ]

    emit('confirm', attachments)

    loading.value = false

    return true
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  loading.value = false
}

const handleSelectAsset = (item: any) => {
  assets.value.selected = item
}

const handleChangedTab = (tabData: any) => {
  tab.value = tabData?.tab?.hash?.replace('#', '')
}

defineExpose({ open })
</script>
